import { ResultOf } from "@graphql-typed-document-node/core";
import { useSuspenseQuery } from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";

import { Section, SectionHeader } from "@roqua/components/sections";

import { CompletedResponseList } from "@/components/CompletedResponses/CompletedResponseList";
import { ErrorPage } from "@/components/ErrorPage";
import { PendingResponses } from "@/components/PendingResponses";
import { ProtocolSubscriptionTimelineItem } from "@/components/ProtocolSubscriptions/ProtocolSubscriptionTimelineItem";
import { ReportList } from "@/components/ReportList";
import { SelfInitiables } from "@/components/SelfInitiables";
import { graphql } from "@/gql";
import { useVerifiedSession } from "@/session";
import { toError } from "@/lib/utils";

const document = graphql(/* GraphQL */ `
  query portalPage($respondentId: ID!) {
    respondent(id: $respondentId) {
      responses(open: true, first: 5) {
        totalCount
        ...PendingResponses_Fragment
      }

      reports(first: 5) {
        totalCount
        ...ReportList_Fragment
      }

      selfInitiableMeasuredQuestionnaires {
        totalCount
        ...SelfInitiables_Fragment
      }

      protocolSubscriptions {
        totalCount
        ...ProtocolSubscriptionTimelineItem_Fragment
      }

      completedResponses: responses(completed: true, first: 3, orderDescending: true) {
        totalCount
        nodes {
          id
          completedAt
          questionnaire {
            name
          }
        }
      }
    }
  }
`);

export type CompletedResponses = ResultOf<typeof document>["respondent"]["completedResponses"];

export const Route = createFileRoute("/portal/")({
  component: RouteComponent,
  errorComponent: ({ error }) => <ErrorPage message={toError(error).message} />,
});

function RouteComponent() {
  const { client, user } = useVerifiedSession();

  const {
    data: { respondent },
  } = useSuspenseQuery({
    queryKey: ["Portal", user.id],
    queryFn: () =>
      client.request(document, {
        respondentId: user.id,
      }),
  });

  const anythingToFillOut =
    respondent.responses.totalCount + respondent.selfInitiableMeasuredQuestionnaires.totalCount > 0;
  const hasActiveProtocolSubscriptions = respondent.protocolSubscriptions.totalCount > 0;

  return (
    <main className="">
      {anythingToFillOut && (
        <Section variant="header">
          <SectionHeader header="Vragenlijsten die je nu kunt invullen" />
          <PendingResponses pendingResponses={respondent.responses} userId={user.id} />
          <SelfInitiables
            selfInitiableMeasuredQuestionnaires={respondent.selfInitiableMeasuredQuestionnaires}
          />
        </Section>
      )}
      {!anythingToFillOut && (
        <Section variant="header">
          <h2 className="mb-4 text-xl">Op dit moment zijn er geen vragenlijsten in te vullen.</h2>
        </Section>
      )}

      {hasActiveProtocolSubscriptions && (
        <Section variant="middle">
          <SectionHeader header="Lopende protocollen" />
          <ProtocolSubscriptionTimelineItem
            activeProtocolSubscriptions={respondent.protocolSubscriptions}
          />
        </Section>
      )}

      <Section variant="content">
        {respondent.reports.totalCount > 0 && (
          <>
            <SectionHeader header="Jouw rapporten" />
            <ReportList reports={respondent.reports} />
          </>
        )}

        {respondent.completedResponses.nodes.length > 0 && (
          <>
            <SectionHeader header="Recente uitkomsten" />
            <CompletedResponseList responses={respondent.completedResponses} />
          </>
        )}
      </Section>
    </main>
  );
}
